import { RefObject } from 'preact';
import { useLayoutEffect, useMemo, useState, useRef, useCallback } from 'preact/hooks';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { autoUpdate, arrow } from '@floating-ui/dom';
import {
  useFloating,
  shift,
  flip,
  offset,
  limitShift,
  PositionData
} from '../hooks/UNSAFE_useFloating';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { Offset, Placement, Coords, VirtualElement, Rect } from '../hooks/UNSAFE_useFloating';
import { useOutsideClick } from '../hooks/UNSAFE_useOutsideClick';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { FloatingTail, renderTail } from './FloatingTail';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children' | 'class'>;

type FlipOptions = {
  mainAxis?: boolean; // This is the main axis in which overflow is checked to perform a flip. Default true.
  crossAxis?: boolean; // This is the cross axis in which overflow is checked to perform a flip, the opposite axis of mainAxis. Default false.
};

type ShiftOptions = {
  mainAxis?: boolean; // This is the main axis in which shifting is applied. Default true.
  crossAxis?: boolean; // This is the cross axis in which shifting is applied, the opposite axis of mainAxis. Default false
};

type Props = IntrinsicProps & {
  /**
   * Placement of the floating element relative to the trigger.
   * Possible values are:
   * <p> <code>'top' </code> <code>'top-start' </code> <code> 'top-end' </code> <code> 'top-start-corner' </code> <code> 'top-end-corner' </code>
   * <code> 'end' </code> <code> 'end-top'  </code> <code> 'end-bottom' </code> <code> 'end-top-corner' </code> <code> 'end-bottom-corner' </code>
   * <code> 'bottom'  </code> <code> 'bottom-start'  </code> <code> 'bottom-end' </code> <code> 'bottom-start-corner' </code> <code> 'bottom-end-corner' </code>
   * <code> 'start' </code> <code> 'start-top'  </code> <code> 'start-bottom' </code> <code> 'start-top-corner' </code> <code> 'start-bottom-corner' </code>
   * </p>
   */
  placement?: Placement;

  /**
   * Trigger element reference.
   */
  anchorRef: RefObject<Element | Coords>;

  /**
   * Placement offset.
   * Offset displaces the floating element from its core placement along the specified axes.
   * Offset could be a number, object or a function.
   * A number represents the distance (gutter or margin) between the floating element and the reference element.
   * This is shorthand for mainAxis.
   * <pre>
   *   {
   *     mainAxis?: number;
   *     crossAxis?: number;
   *   }
   * </pre>
   * <p><code>mainAxis</code> represents the distance (gutter or margin) between the floating element and the reference element.</p>
   * <p><code>crossAxis</code> represents the shifting between the floating element and the reference element.<p>
   * You may also pass a function which returns the previously listed values — this enables you to read the dimensions of the reference or floating elements and the current placement.
   * <pre>
   * (args: {
   *   floating: Rect;
   *   reference: Rect;
   *   placement: Placement;
   * }) => OffsetValue
   * </pre>
   */
  offsetValue?: Offset;

  /**
   * Click outside callback.
   */
  onClickOutside?: (event: MouseEvent) => void;

  /**
   * Specifies the flipping behavior along the respective axis.
   * When true, the floating element will be flipped to prevent overflow.
   * Default values are 'true' for mainAxis and 'false' for crossAxis.
   */
  flipOptions?: FlipOptions;

  /**
   * Specifies the shift behavior along the respective axis.
   * When true, the floating element will shift to prevent overflow.
   * Default values are 'true' for mainAxis and 'false' for crossAxis.
   */
  shiftOptions?: ShiftOptions;

  /**
   *  Triggered when placement or coordinates are changed after collision is detected
   */
  onPosition?: (data: PositionData) => void;

  /**
   * Tail
   */
  tail?: 'none' | 'simple';
};

function isElement(value: any): value is Element {
  return value instanceof Element;
}
/**
 * A floating component allows the user to create a floating element that is displayed on the the "top layer" of all other elements.
 *  The Floating component is an advanced component and is used underneath Popup, Tooltip, SelectMultiple, Menu and other components.
 */
export const Floating = forwardRef(
  (
    {
      children,
      onClickOutside,
      placement = 'bottom',
      anchorRef,
      offsetValue,
      class: className,
      tail = 'none',
      flipOptions = { mainAxis: true, crossAxis: false },
      shiftOptions = { mainAxis: true, crossAxis: false },
      onPosition
    }: Props,
    ref: ForwardedRef<HTMLElement | null>
  ) => {
    const arrowRef = useRef<HTMLDivElement>(null);

    const [previousAnchor, setPreviousAnchor] = useState<Element | Coords | null>(null);

    // we need to be sure anchorRef.current exists in first call
    // to be able to set the previousAnchor state
    useLayoutEffect(() => {
      setPreviousAnchor(anchorRef.current);
    }, []);

    // In case anchorRef.current changes and we need to update floating position
    // when it changes, we need to have copy in the state.
    // For example when anchorRef is Coords( virtual element)
    if (anchorRef.current !== previousAnchor) {
      setPreviousAnchor(anchorRef.current);
    }

    const { direction } = useUser();
    const isRtl = direction === 'rtl';

    const _onPosition = useCallback(
      (data: PositionData) => {
        renderTail(data, anchorRef, arrowRef, tail, isRtl);
        onPosition?.(data);
      },
      [placement, anchorRef, arrowRef, tail, onPosition]
    );

    const { x, y, reference, floating, refs } = useFloating({
      placement: placement,
      middleware: [
        offset(offsetValue),
        flip({
          mainAxis: flipOptions.mainAxis,
          crossAxis: flipOptions.crossAxis
        }),
        shift({
          mainAxis: shiftOptions.mainAxis,
          crossAxis: shiftOptions.crossAxis,
          limiter: limitShift({
            mainAxis: shiftOptions.mainAxis,
            crossAxis: shiftOptions.crossAxis
          })
        }),
        ...(arrowRef.current
          ? [
              arrow({
                element: arrowRef.current
              })
            ]
          : [])
      ],
      whileElementsMounted: autoUpdate,
      onPosition: _onPosition
    });

    useOutsideClick({ isDisabled: false, ref: refs.floating, handler: onClickOutside });

    useLayoutEffect(() => {
      if (isElement(previousAnchor)) {
        const element = previousAnchor as Element;
        reference(element);
        return;
      }
      const coords = previousAnchor as Coords;
      const virtualEl: VirtualElement = {
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: coords!.x,
            y: coords!.y,
            top: coords!.y,
            left: coords!.x,
            right: coords!.x,
            bottom: coords!.y
          };
        }
      };
      if (coords && coords.x != null) {
        reference(virtualEl);
      }
    }, [previousAnchor, reference]);

    const stableRef = useMemo(() => mergeRefs(ref, floating), [ref, floating]);
    const content = (
      <div
        class={className}
        ref={stableRef}
        style={{
          display: 'block',
          visibility: x == null && y == null ? 'hidden' : 'visible',
          position: 'absolute',
          top: y ?? '',
          left: x ?? ''
        }}>
        {children}
        <FloatingTail ref={arrowRef} />
      </div>
    );
    return content;
  }
);

export { Offset, Placement, Coords, Rect, FlipOptions, ShiftOptions, PositionData };
